// UI copy for both languages. Keys are dotted by area. Placeholders use
// {name} and are filled by t(key, { name }). `es` is typed against `en` so the
// two stay in lockstep -- a missing or extra key is a compile error.

const en = {
  "common.loading": "Loading…",
  "common.tryAgain": "Try again",
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.signOut": "Sign out",

  "header.title": "Family Videos",
  "header.signedInAs": "Signed in as",
  "header.yourGroups": "Your family groups",

  "theme.toDark": "Switch to dark mode",
  "theme.toLight": "Switch to light mode",

  "lang.toSpanish": "Switch to Spanish",
  "lang.toEnglish": "Switch to English",

  "trimmer.heading": "Trim a clip to share",
  "trimmer.clipStart": "Clip start",
  "trimmer.clipEnd": "Clip end",
  "trimmer.clipPosition": "Clip position",
  "trimmer.start": "Start",
  "trimmer.end": "End",
  "trimmer.setBound": "Set {label}",
  "trimmer.nudgeBack": "Nudge {label} back half a second",
  "trimmer.nudgeForward": "Nudge {label} forward half a second",
  "trimmer.zoomedLabel": "Zoomed to clip",
  "trimmer.wholeLabel": "Whole video",
  "trimmer.instructions":
    "Scrub to a moment, then tap Set start or Set end. Drag the handles or use ± to fine-tune — clips can be up to 15 minutes.",
  "trimmer.preview": "Preview clip",
  "trimmer.zoomIn": "Zoom to clip",
  "trimmer.zoomOut": "Show whole video",
  "trimmer.createLink": "Create share link",
  "trimmer.createError": "Couldn't create the link. Try again.",
  "trimmer.linkAria": "Shareable clip link",
  "trimmer.shareInfo":
    "Anyone with this link can watch this {length} clip — no sign-in needed. The link works for {days} days.",
  "trimmer.closeVideo": "Close video",
  "trimmer.videoUnsupported": "Your browser can't play this video.",
  "trimmer.downloadInstead": "Download the video",

  "shared.loading": "Loading clip…",
  "shared.processingTitle": "Preparing your clip…",
  "shared.processingBody": "This takes a few seconds.",
  "shared.errorTitle": "This link doesn't work",
  "shared.errorBody":
    "It may be wrong or expired. Ask whoever sent it for a new one.",
  "shared.clipLabel": "Clip",
  "shared.replay": "Replay clip",
  "shared.footer": "Shared from Famflix",

  "publicVideo.loading": "Loading video…",
  "publicVideo.errorTitle": "This link doesn't work",
  "publicVideo.errorBody":
    "It may have been unpublished, or the link is wrong. Ask whoever sent it for a new one.",

  "publicShare.label": "Public link",
  "publicShare.linkAria": "Public video link",
  "publicShare.show": "Show link",
  "publicShare.info":
    "Anyone with this link can watch the full video. No sign-in needed.",
  "publicShare.error": "Couldn't update. Try again.",

  "videoItem.failed": "Couldn't load video",
  "videoItem.watch": "Watch",
  "videoItem.download": "Download video",

  "error.title": "Something went wrong",

  "logout.title": "You're signed out",
  "logout.body":
    "We'll take you back to the home page. Sign in any time to keep watching.",
  "logout.signBackIn": "Sign back in",

  "tagFilter.heading": "Filter by tags",
  "tagFilter.clearAll": "Clear all",
  "tagFilter.clear": "Clear",
  "tagFilter.activeFilters": "Active filters:",
  "tagFilter.removeFilter": "Remove {tag}: {value} filter",

  "filterResults.showingAll": "Showing all {count} videos",
  "filterResults.noMatch": "No videos match your filters",
  "filterResults.noMatchBody": "Remove a filter to see more.",
  "filterResults.showingOf": "Showing {filtered} of {total} videos",
  "filterResults.filteredBadge": "Filtered",

  "videoList.errorLoading": "Couldn't load videos",
  "videoList.noVideos": "No videos yet",
  "videoList.noVideosBody": "There's nothing to show right now.",
  "videoList.noFound": "No videos found",
  "videoList.noFoundBody": "Adjust your filters to see more.",

  "videoThumbnails.none": "No thumbnails",

  "app.signingIn": "Signing in…",
  "app.redirecting": "Redirecting…",
  "app.authErrorTitle": "Sign-in error",
  "app.authErrorBody": "Something went wrong signing in. Try again.",
} as const;

const es: Record<keyof typeof en, string> = {
  "common.loading": "Cargando…",
  "common.tryAgain": "Reintentar",
  "common.copy": "Copiar",
  "common.copied": "Copiado",
  "common.signOut": "Cerrar sesión",

  "header.title": "Videos familiares",
  "header.signedInAs": "Sesión iniciada como",
  "header.yourGroups": "Tus grupos",

  "theme.toDark": "Cambiar a modo oscuro",
  "theme.toLight": "Cambiar a modo claro",

  "lang.toSpanish": "Cambiar a español",
  "lang.toEnglish": "Cambiar a inglés",

  "trimmer.heading": "Recorta un clip para compartir",
  "trimmer.clipStart": "Inicio del clip",
  "trimmer.clipEnd": "Fin del clip",
  "trimmer.clipPosition": "Posición del clip",
  "trimmer.start": "Inicio",
  "trimmer.end": "Fin",
  "trimmer.setBound": "Marcar {label}",
  "trimmer.nudgeBack": "Atrasar {label} medio segundo",
  "trimmer.nudgeForward": "Adelantar {label} medio segundo",
  "trimmer.zoomedLabel": "Acercado al clip",
  "trimmer.wholeLabel": "Video completo",
  "trimmer.instructions":
    "Mueve la línea a un momento y toca Marcar inicio o Marcar fin. Arrastra los controles o usa ± para ajustar — los clips pueden durar hasta 15 minutos.",
  "trimmer.preview": "Ver el clip",
  "trimmer.zoomIn": "Acercar al clip",
  "trimmer.zoomOut": "Ver video completo",
  "trimmer.createLink": "Crear enlace",
  "trimmer.createError": "No se pudo crear el enlace. Reintenta.",
  "trimmer.linkAria": "Enlace del clip",
  "trimmer.shareInfo":
    "Cualquiera con este enlace puede ver este clip de {length} — sin iniciar sesión. El enlace funciona por {days} días.",
  "trimmer.closeVideo": "Cerrar video",
  "trimmer.videoUnsupported": "Tu navegador no puede reproducir este video.",
  "trimmer.downloadInstead": "Descargar el video",

  "shared.loading": "Cargando clip…",
  "shared.processingTitle": "Preparando tu clip…",
  "shared.processingBody": "Esto tarda unos segundos.",
  "shared.errorTitle": "Este enlace no funciona",
  "shared.errorBody":
    "Puede estar mal o vencido. Pídele uno nuevo a quien te lo envió.",
  "shared.clipLabel": "Clip",
  "shared.replay": "Repetir clip",
  "shared.footer": "Compartido desde Famflix",

  "publicVideo.loading": "Cargando video…",
  "publicVideo.errorTitle": "Este enlace no funciona",
  "publicVideo.errorBody":
    "Puede que ya no sea público o que el enlace esté mal. Pídele uno nuevo a quien te lo envió.",

  "publicShare.label": "Enlace público",
  "publicShare.linkAria": "Enlace del video público",
  "publicShare.show": "Ver enlace",
  "publicShare.info":
    "Cualquiera con este enlace puede ver el video completo. Sin iniciar sesión.",
  "publicShare.error": "No se pudo actualizar. Reintenta.",

  "videoItem.failed": "No se pudo cargar el video",
  "videoItem.watch": "Ver",
  "videoItem.download": "Descargar video",

  "error.title": "Algo salió mal",

  "logout.title": "Cerraste sesión",
  "logout.body":
    "Te llevaremos a la página principal. Inicia sesión cuando quieras para seguir viendo.",
  "logout.signBackIn": "Iniciar sesión",

  "tagFilter.heading": "Filtrar por etiquetas",
  "tagFilter.clearAll": "Borrar todo",
  "tagFilter.clear": "Borrar",
  "tagFilter.activeFilters": "Filtros activos:",
  "tagFilter.removeFilter": "Quitar filtro {tag}: {value}",

  "filterResults.showingAll": "Mostrando los {count} videos",
  "filterResults.noMatch": "Ningún video coincide con tus filtros",
  "filterResults.noMatchBody": "Quita un filtro para ver más.",
  "filterResults.showingOf": "Mostrando {filtered} de {total} videos",
  "filterResults.filteredBadge": "Filtrado",

  "videoList.errorLoading": "No se pudieron cargar los videos",
  "videoList.noVideos": "Aún no hay videos",
  "videoList.noVideosBody": "No hay nada que mostrar por ahora.",
  "videoList.noFound": "No se encontraron videos",
  "videoList.noFoundBody": "Ajusta tus filtros para ver más.",

  "videoThumbnails.none": "Sin miniaturas",

  "app.signingIn": "Iniciando sesión…",
  "app.redirecting": "Redirigiendo…",
  "app.authErrorTitle": "Error al iniciar sesión",
  "app.authErrorBody": "Algo salió mal al iniciar sesión. Reintenta.",
};

export const translations = { en, es };
export type Language = keyof typeof translations;
export type TranslationKey = keyof typeof en;
